/**
 * The display keys, in a module with no imports so a test can reach them.
 *
 * Neither entity carries a human key the hub generated: a brief has only its id,
 * and a task's `key` is graph-local (`T-1`, `T-2`), unique inside its brief and
 * nowhere else. These functions are the whole convention for turning that
 * into something a person can say out loud and search for.
 *
 * They are mirrored from `@dyspatch/core`'s `keys.ts`, which the worker uses to
 * put the same job key on a PR title, a branch and a commit trailer. The mirror
 * exists because importing core values here pulls `node:crypto` into the bundle;
 * `packages/hub/test/keys.test.ts` pins the two copies to each other.
 */

/** A brief has no human key, so this is its display key: B- plus four. */
export function shortKey(id: string, prefix = 'B'): string {
  return `${prefix}-${String(id)
    .replace(/[^a-z0-9]/gi, '')
    .slice(-4)}`;
}

/** What goes between the job and the step: `B-swh3 / T-1`. */
export const KEY_SEPARATOR = ' / ';

/**
 * A task key qualified by its brief: `B-swh3 / T-1`.
 *
 * Used everywhere a task is listed outside its own brief - the Tasks page, the
 * review queue, the failures panel, the palette - because the bare key is not
 * unique there and two rows reading `T-1` are two rows a person cannot tell
 * apart. Inside a brief the bare key is the right one.
 */
export function qualifiedKey(briefId: string, key: string, separator = KEY_SEPARATOR): string {
  return `${shortKey(briefId)}${separator}${key}`;
}

/**
 * The same identity, spelled for GitHub: `B-swh3 T-1`.
 *
 * The console does not render this form; it is here so the mirror is complete
 * and the pin test covers everything the worker writes onto a PR.
 */
export function githubKey(briefId: string, key: string): string {
  return qualifiedKey(briefId, key, ' ');
}
